import { useEffect, useState } from "react";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import {
  Copy, Check, Building2, Smartphone, Wallet, ArrowRight,
  Eye, EyeOff, Share2, ShieldCheck, Zap, CheckCircle2,
  Clock, Loader2, WifiOff,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CURRENCIES } from "@/lib/constants";
import { FCSBadge } from "@/components/fcs";
import { api } from "@/lib/api";
import { formatAmount } from "@/lib/format";

const ez = mmEase.expo;
const reducedMotion =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);

const THREAD_PATH_SM = "M 0 14 C 80 14 120 8 200 14 C 280 20 360 10 440 14";

const ACCOUNTS = {
  USD: { bank: "Citibank N.A.",    routing: "021000089",  account: "8049-2210-3367", swift: "CITIUS33" },
  EUR: { bank: "Deutsche Bank AG", iban: "DE89 3704 0044 0532 0130 00",               swift: "DEUTDEFF" },
  GBP: { bank: "Barclays Bank UK", sort: "20-00-00",      account: "55779912",        swift: "BARCGB22" },
  CAD: { bank: "RBC Royal Bank",   transit: "00012-003",  account: "1003892213",      swift: "ROYCCAT2" },
  NGN: { bank: "GTBank Nigeria",   account: "0123456789",                              swift: "GTBINGLA" },
  GHS: { bank: "Ecobank Ghana",    account: "9876543210",                              swift: "ECOCGHAC" },
  KES: { bank: "KCB Bank Kenya",   account: "1109876543",                              swift: "KCBLKENX" },
  RON: { bank: "Banca Transilvania", iban: "RO49 AAAA 1B31 0075 9384 0000",             swift: "BTRLRO22" },
};

const CHANNELS = [
  {
    icon: Building2, color: "#10B981",
    title: "Bank transfer",
    desc:  "Use the account details above. Local rails typically arrive same day; SWIFT transfers take 1–3 business days.",
  },
  {
    icon: Smartphone, color: "#7C3AED",
    title: "Mobile money",
    desc:  "Receive on M-Pesa, MTN MoMo, Airtel Money. Auto-converted to your wallet.",
  },
  {
    icon: Wallet, color: "#06B6D4",
    title: "Wallet to wallet",
    desc:  "A friend can send instantly to your MoneyMeet email address.",
  },
];

const TL_STEPS = [
  { n: 1, label: "Sender initiates",  sub: "The transfer starts on their end",           color: "#10B981", time: "Instant" },
  { n: 2, label: "Processing",        sub: "Funds in transit via SWIFT or local rails",  color: "#06B6D4", time: "Minutes to 1 business day" },
  { n: 3, label: "Received",          sub: "We receive the funds from the sending bank", color: "#10B981", time: "Settlement complete" },
  { n: 4, label: "Added to wallet",   sub: "Your balance updates automatically",         color: "#10B981", time: "Within minutes of receipt" },
  { n: 5, label: "Spendable",         sub: "Ready to send, convert, or withdraw",        color: "#10B981", time: "Immediate" },
];

const STATUS_VARIANT = {
  pending:    { variant: "processing", label: "Expected"    },
  processing: { variant: "processing", label: "Processing"  },
  completed:  { variant: "success",    label: "Received"    },
  available:  { variant: "success",    label: "Available"   },
  spendable:  { variant: "success",    label: "Spendable"   },
  delayed:    { variant: "pending",    label: "Delayed"     },
  failed:     { variant: "failure",    label: "Failed"      },
};

function usePrivacyMode() {
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("mm-privacy-mode") === "true"; } catch { return false; }
  });
  const toggle = () =>
    setHidden((h) => {
      const next = !h;
      try { localStorage.setItem("mm-privacy-mode", String(next)); } catch {}
      return next;
    });
  return [hidden, toggle];
}

export default function Receive() {
  const { user } = useAuth();
  const [currency,     setCurrency]    = useState("GBP");
  const [copied,       setCopied]      = useState("");
  const [copyAllDone,  setCopyAllDone] = useState(false);
  const [sharing,      setSharing]     = useState(false);
  const [incomingTxs,  setIncomingTxs] = useState([]);
  const [txError,      setTxError]     = useState(false);
  const [privacyHidden, togglePrivacy] = usePrivacyMode();

  const a = ACCOUNTS[currency] || {};

  useEffect(() => {
    api
      .get("/transactions")
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : data.results || [];
        setIncomingTxs(
          all
            .filter(
              (t) =>
                t.type === "receive" ||
                t.direction === "incoming" ||
                Number(t.receive_amount) > 0,
            )
            .slice(0, 3),
        );
      })
      .catch(() => {
        setTxError(true);
        toast.error("Could not load recent transfers. Check your connection.");
      });
  }, []);

  const payRef = `MMR-${(user?.id || "").slice(0, 8).toUpperCase()}`;

  const rows = [
    { label: "Account holder",   value: user?.full_name || "MoneyMeet User", key: "name",    mask: false },
    { label: "Bank",             value: a.bank,      key: "bank",    mask: false },
    a.iban    && { label: "IBAN",             value: a.iban,    key: "iban",    mono: true, mask: true },
    a.account && { label: "Account number",  value: a.account, key: "account", mono: true, mask: true },
    a.routing && { label: "Routing (ABA)",   value: a.routing, key: "routing", mono: true, mask: true },
    a.sort    && { label: "Sort code",       value: a.sort,    key: "sort",    mono: true, mask: true },
    a.transit && { label: "Transit",         value: a.transit, key: "transit", mono: true, mask: true },
    a.swift   && { label: "SWIFT / BIC",     value: a.swift,   key: "swift",   mono: true, mask: false },
    { label: "Payment reference", value: payRef,     key: "ref",     mono: true, mask: false },
  ].filter(Boolean);

  const maskValue = (value, mask) => {
    if (!privacyHidden || !mask) return value;
    const str = String(value || "");
    return "•••• " + str.slice(-4);
  };

  const announce = (msg) => {
    const el = document.getElementById("receive-live");
    if (el) el.textContent = msg;
  };

  const copy = (value, key) => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(""), 1800);
  };

  const copyAll = () => {
    const text = rows.map((r) => `${r.label}: ${r.value}`).join("\n");
    navigator.clipboard.writeText(text).catch(() => {});
    setCopyAllDone(true);
    announce("All account details copied");
    setTimeout(() => setCopyAllDone(false), 2000);
  };

  const shareDetails = async () => {
    const text = rows.map((r) => `${r.label}: ${r.value}`).join("\n");
    const title = `My ${currency} account details — MoneyMeet`;
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({ title, text });
        announce("Details shared successfully");
      } else {
        await navigator.clipboard.writeText(`${title}\n\n${text}`);
        toast.success("Account details copied to clipboard");
        announce("Account details copied to clipboard");
      }
    } catch {}
    finally { setSharing(false); }
  };

  const hasPending   = incomingTxs.some((t) => ["pending", "processing"].includes(t.status));
  const hasAvailable = incomingTxs.some((t) => ["completed", "available", "spendable"].includes(t.status));

  const qrValue = `moneymeet://receive?ref=${payRef}&currency=${currency}`;

  return (
    <div className="space-y-6">

      {/* ARIA live region */}
      <div id="receive-live" role="status" aria-live="polite" aria-atomic="true" className="sr-only" />

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Receive</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">
          Ready when they send.
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Your own dedicated account in every currency. Share these details to receive funds.
        </p>
      </motion.div>

      {/* ── Currency pills ──────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: ez, delay: 0.06 }}
        className="flex flex-wrap gap-2"
      >
        {CURRENCIES.map((c) => (
          <button
            key={c.code}
            data-testid={`receive-cur-${c.code}`}
            onClick={() => setCurrency(c.code)}
            aria-pressed={currency === c.code}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
              currency === c.code
                ? "bg-emerald text-white shadow-emerald-sm"
                : "bg-white border border-border/70 text-navy hover:border-emerald/30 hover:bg-emerald/[0.04] hover:text-emerald"
            }`}
          >
            <span>{c.flag}</span>
            <span>{c.code}</span>
          </button>
        ))}
      </motion.div>

      {/* ── Main grid ───────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-3 gap-5">

        {/* ── Account details card ── */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currency}
            className="lg:col-span-2 card-elevated overflow-hidden"
            initial={{ opacity: 0, x: 14, filter: "blur(3px)" }}
            animate={{ opacity: 1, x: 0,  filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -14,   filter: "blur(3px)" }}
            transition={{ duration: 0.3, ease: ez }}
          >
            {/* Hero */}
            <div className="brand-gradient px-6 pt-6 pb-5 relative overflow-hidden">
              <div
                className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl pointer-events-none"
                style={{ background: "rgba(16,185,129,0.18)" }}
              />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              {/* Thread */}
              {!reducedMotion && (
                <svg
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 w-full pointer-events-none"
                  height="28"
                  viewBox="0 0 440 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ opacity: 0.5 }}
                >
                  <defs>
                    <filter id="th-rec-glow">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
                      <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                    <style>{`@keyframes th-rec-dash{from{stroke-dashoffset:40}to{stroke-dashoffset:0}}`}</style>
                  </defs>
                  <path d={THREAD_PATH_SM} stroke="rgba(16,185,129,0.25)" strokeWidth="1" fill="none" />
                  <path
                    d={THREAD_PATH_SM}
                    stroke="rgba(16,185,129,0.7)"
                    strokeWidth="1.5"
                    fill="none"
                    strokeDasharray="6 5"
                    filter="url(#th-rec-glow)"
                    style={{ animation: "th-rec-dash 2.0s linear infinite" }}
                  />
                  <circle r="5" fill="rgba(16,185,129,0.18)" filter="url(#th-rec-glow)">
                    <animateMotion dur="2.0s" repeatCount="indefinite" path={THREAD_PATH_SM} />
                  </circle>
                  <circle r="2.5" fill="#10B981">
                    <animateMotion dur="2.0s" repeatCount="indefinite" path={THREAD_PATH_SM} />
                  </circle>
                  <circle r="1.2" fill="#fff">
                    <animateMotion dur="2.0s" repeatCount="indefinite" path={THREAD_PATH_SM} />
                  </circle>
                </svg>
              )}

              {/* Privacy toggle */}
              <button
                data-testid="receive-privacy-toggle"
                onClick={() => {
                  togglePrivacy();
                  announce(privacyHidden ? "Account details visible" : "Account details hidden");
                }}
                aria-label={privacyHidden ? "Show account details" : "Hide account details"}
                className="absolute top-4 right-5 w-8 h-8 grid place-items-center rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
              >
                <AnimatePresence mode="wait">
                  {privacyHidden ? (
                    <motion.span
                      key="off"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <EyeOff size={14} className="text-white/70" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="on"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Eye size={14} className="text-white/70" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>

              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1 relative">
                Bank transfer
              </p>
              <h3 className="font-display text-xl font-black text-white relative">
                {currency} account details
              </h3>
              <p className="text-white/45 text-sm mt-1 relative">
                Funds arrive in your {currency} wallet, usually within minutes.
              </p>
            </div>

            {/* Detail rows */}
            <div className="divide-y divide-border/50">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between px-6 py-3.5 gap-3 hover:bg-secondary/40 transition-colors"
                >
                  <p className="text-[11px] font-semibold text-muted-foreground shrink-0 w-36">
                    {row.label}
                  </p>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <p
                      className={`text-sm font-semibold text-navy text-right truncate ${
                        row.mono ? "font-mono" : ""
                      }`}
                    >
                      {maskValue(row.value, row.mask)}
                    </p>
                    <button
                      onClick={() => {
                        copy(row.value, row.key);
                        announce(`${row.label} copied`);
                      }}
                      aria-label={`Copy ${row.label}`}
                      className="w-7 h-7 shrink-0 grid place-items-center rounded-lg text-muted-foreground hover:text-emerald hover:bg-emerald/[0.08] transition-all"
                    >
                      {copied === row.key ? (
                        <Check size={13} className="text-emerald" strokeWidth={2.5} />
                      ) : (
                        <Copy size={13} />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* RCI strip */}
            <div className="px-6 py-3 border-t border-border/40 flex flex-wrap gap-2">
              {[
                { key: "verified", Icon: ShieldCheck,  label: "Account verified", color: "#10B981", show: true },
                { key: "ready",    Icon: Zap,          label: "Ready to receive", color: "#10B981", show: true },
                { key: "pending",  Icon: Clock,        label: "Funds pending",    color: "#F59E0B", show: hasPending },
                { key: "available",Icon: CheckCircle2, label: "Funds available",  color: "#10B981", show: hasAvailable },
                { key: "secure",   Icon: ShieldCheck,  label: "Secure account",   color: "#10B981", show: true },
              ]
                .filter((r) => r.show)
                .map(({ key, Icon, label, color }, i) => (
                  <motion.div
                    key={key}
                    data-testid={`receive-confidence-${key}`}
                    aria-hidden="true"
                    initial={reducedMotion ? {} : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.3, ease: ez }}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold"
                    style={{ background: `${color}12`, color }}
                  >
                    <Icon size={10} strokeWidth={2.5} />
                    {label}
                  </motion.div>
                ))}
            </div>

            {/* Action buttons */}
            <div className="px-6 pb-5 pt-3 flex flex-wrap gap-3">
              <button
                data-testid="receive-copy-all"
                onClick={copyAll}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                  copyAllDone
                    ? "bg-emerald/10 border-emerald/30 text-emerald"
                    : "bg-secondary/60 border-border/60 text-navy hover:bg-emerald/[0.06] hover:border-emerald/30 hover:text-emerald"
                }`}
              >
                {copyAllDone
                  ? <Check size={13} strokeWidth={2.5} />
                  : <Copy size={13} />}
                {copyAllDone ? "Copied!" : "Copy all details"}
              </button>
              <button
                data-testid="receive-share"
                onClick={shareDetails}
                disabled={sharing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border bg-secondary/60 border-border/60 text-navy hover:bg-emerald/[0.06] hover:border-emerald/30 hover:text-emerald transition-all disabled:opacity-60"
              >
                {sharing
                  ? <Loader2 size={13} className="animate-spin" />
                  : <Share2 size={13} />}
                Share
              </button>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ── Sidebar ── */}
        <div className="space-y-3">

          {/* QR card */}
          <motion.div
            className="card-float p-5"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.36, ease: ez, delay: 0.08 }}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">
              Scan to pay
            </p>
            <div className="flex justify-center mb-3">
              <div
                data-testid="receive-qr"
                className="relative w-[120px] h-[120px] rounded-2xl overflow-hidden border border-border/60 bg-white p-2"
              >
                {!privacyHidden ? (
                  <QRCodeSVG
                    value={qrValue}
                    size={104}
                    bgColor="transparent"
                    fgColor="#0B1D3A"
                    level="M"
                    aria-label={`QR code for ${currency} account`}
                    style={{ width: "100%", height: "100%" }}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                    <EyeOff size={18} className="text-muted-foreground mb-1" />
                    <span className="text-[9px] font-bold text-muted-foreground">Privacy on</span>
                  </div>
                )}
              </div>
            </div>
            <p className="text-[10.5px] text-center text-muted-foreground leading-snug">
              Scan with your banking app or camera
            </p>
          </motion.div>

          {/* Activity Timeline */}
          <motion.div
            className="card-elevated p-5"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.36, ease: ez, delay: 0.13 }}
          >
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4">
              How receiving works
            </p>
            <div data-testid="receive-timeline">
              {TL_STEPS.map((step, i) => (
                <div key={step.n} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0"
                      style={{ background: `${step.color}18`, color: step.color }}
                    >
                      {step.n}
                    </div>
                    {i < TL_STEPS.length - 1 && (
                      <div
                        className="w-px flex-1 min-h-[18px] my-1"
                        style={{ background: `${step.color}28` }}
                      />
                    )}
                  </div>
                  <div className={`${i < TL_STEPS.length - 1 ? "pb-3.5" : "pb-1"} pt-0.5`}>
                    <p className="text-[11.5px] font-bold text-navy leading-tight">{step.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-snug">{step.sub}</p>
                    <p className="font-mono text-[9px] mt-0.5" style={{ color: step.color }}>
                      {step.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Channel cards */}
          {CHANNELS.map(({ icon: Icon, color, title, desc }, i) => (
            <motion.div
              key={title}
              className="card-float p-5 group relative overflow-hidden"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.36, ease: ez, delay: 0.18 + i * 0.05 }}
              whileHover={{ y: -2 }}
            >
              <div
                className="absolute -top-8 -right-8 w-28 h-28 rounded-full blur-2xl opacity-[0.10] group-hover:opacity-[0.18] transition-opacity duration-500 pointer-events-none"
                style={{ background: color }}
              />
              <div
                className="w-10 h-10 rounded-xl grid place-items-center mb-3.5 relative"
                style={{ background: `${color}16`, color }}
              >
                <Icon size={17} strokeWidth={1.8} />
              </div>
              <p className="font-bold text-navy text-sm relative">{title}</p>
              <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed relative">{desc}</p>
            </motion.div>
          ))}

          {/* Send instead */}
          <motion.div
            className="card-elevated p-5"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.36, ease: ez, delay: 0.36 }}
          >
            <p className="font-bold text-navy text-sm mb-2">Want to send instead?</p>
            <p className="text-[12px] text-muted-foreground mb-3.5">
              Move money from any of your wallets to 45+ countries.
            </p>
            <Link to="/app/send" className="btn-primary w-full justify-center text-sm py-2.5">
              Send money <ArrowRight size={13} />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ── Incoming transfers ──────────────────────────────────── */}
      {txError && (
        <div
          role="alert"
          data-testid="receive-transfers-error"
          className="card-elevated p-4 flex items-center gap-3"
          style={{ border: "1px solid rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.04)" }}
        >
          <WifiOff size={16} className="shrink-0" style={{ color: "#EF4444" }} />
          <p className="text-sm text-navy font-semibold">Could not load recent incoming transfers.</p>
        </div>
      )}
      {!txError && incomingTxs.length > 0 && (
        <motion.section
          data-testid="receive-incoming-transfers"
          aria-label="Incoming transfers"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: ez, delay: 0.2 }}
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold mb-3">
            Recent incoming
          </p>
          <div className="space-y-2">
            {incomingTxs.map((tx) => {
              const sv = STATUS_VARIANT[tx.status] || { variant: "processing", label: tx.status };
              return (
                <div
                  key={tx.id}
                  className="card-base px-5 py-3.5 flex items-center justify-between gap-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-navy truncate">
                      {tx.from_currency} → {tx.to_currency}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                      {tx.reference || (tx.id || "").slice(0, 10)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className="text-sm font-black text-navy font-mono">
                      {formatAmount(tx.receive_amount, tx.to_currency)}
                    </p>
                    <FCSBadge variant={sv.variant} label={sv.label} />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.section>
      )}
    </div>
  );
}
